import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../lib/api'
import { sha256Hex } from '../lib/fileHash'
import { DEFAULT_MAX_AUDIO_BYTES, audioUploadError, normalizedAudioContentType, shouldRefreshCanonicalAudioAfterUpload } from '../lib/audioUpload'
import { audioStore } from '../lib/blobStore'
import { audioCacheKey, downloadAudioFully } from '../lib/audioDownload'
import { nudge, roundTenth } from '../lib/segmentQueue'
import { mergeSegnoWithNeighbor, shiftAllSegnoTimes, splitSegnoAtTime } from '../lib/segnoEdit'
import { automaticSegnosFromMusicStructure, type AutoSplitPreset } from '../lib/musicStructureSegno'
import { canonicalYoutubeUrl, segmentRangeError, youtubeVideoId } from '../lib/youtube'
import { parseSegnoFile, segnosFromFile } from '../lib/segnoFile'
import { clampSeekTime } from '../lib/playbackSeek'
import { authoringSnapshot } from '../lib/authoringDirty'
import { ownerAdsEligible } from '../lib/adEligibility'
import { YouTubeAuthoringPreview, type YouTubeAuthoringPreviewHandle } from './YouTubeAuthoringPreview'
import { PlaybackSeekControls } from './PlaybackSeekControls'
import { VolumeControl } from './PlaybackPreferenceControls'
import { NonPersonalizedAdSlot } from './NonPersonalizedAdSlot'
import { usePlaybackVolume } from '../hooks/usePlaybackVolume'
import type { AccountPlan, AppStatus, Segno, Song, SourceType } from '../types'
import { userMessageForError } from '../lib/userError'
import { useI18n } from '../i18n/useI18n'
import { legalDocumentUrl } from '../lib/legalLocale'

function freshSegno(order: number): Segno {
  return {
    order,
    title: `Segno ${String(order).padStart(2, '0')}`,
    startTime: 0,
    endTime: 1,
    lyrics: '',
  }
}



function editableSegnos(song: Song): Segno[] {
  return [...song.segnos]
    .sort((left, right) => left.order - right.order)
    .map((segno, index) => ({
      order: index + 1,
      title: segno.title,
      startTime: segno.startTime,
      endTime: segno.endTime,
      lyrics: segno.lyrics ?? '',
    }))
}

function songSegnoPayload(segnos: Segno[]) {
  return segnos.map((segno) => ({
    order: segno.order,
    title: segno.title,
    startTime: segno.startTime,
    endTime: segno.endTime,
    lyrics: segno.lyrics ?? '',
  }))
}

export function AuthoringWorkspace({
  appStatus,
  plan,
  initialSongId,
  onEditLoaded,
  onUnsavedChangesChange,
  onCreatedPractice,
}: {
  appStatus: AppStatus | null
  plan: AccountPlan | null
  initialSongId?: number | null
  onEditLoaded?: () => void
  onUnsavedChangesChange?: (dirty: boolean) => void
  onCreatedPractice?: (songId: number) => void
}) {
  const { locale, t } = useI18n()
  const initialSourceType: SourceType = appStatus?.mediaApiEnabled ? 'FILE' : 'YOUTUBE'
  const [sourceType, setSourceType] = useState<SourceType>(() => initialSourceType)
  const [sourceMode, setSourceMode] = useState<SourceType | 'SEGNO_FILE'>(() => initialSourceType)
  const [title, setTitle] = useState('')
  const [mediaFileId, setMediaFileId] = useState<string | null>(null)
  const [youtubeUrl, setYoutubeUrl] = useState('')
  const [youtubeDuration, setYoutubeDuration] = useState<number | null>(null)
  const [fileDuration, setFileDuration] = useState<number | null>(null)
  const [localPreview, setLocalPreview] = useState<string | null>(null)
  const [segnos, setSegnos] = useState<Segno[]>([freshSegno(1)])
  const [uploadProgress, setUploadProgress] = useState(0)
  const [previewDownloadProgress, setPreviewDownloadProgress] = useState<number | null>(null)
  const [previewDownloadState, setPreviewDownloadState] = useState<'idle' | 'waiting' | 'downloading' | 'ready' | 'error'>('idle')
  const [editingShareId, setEditingShareId] = useState<string | null>(null)
  const [uploadCopyConfirmed, setUploadCopyConfirmed] = useState(false)
  const [message, setMessage] = useState('')
  const [copyrightConfirmed, setCopyrightConfirmed] = useState(false)
  const [editingSongId, setEditingSongId] = useState<number | null>(null)
  // 신규 제작자에게만 단계별 안내를 보여 줍니다. 기존 곡 편집 흐름은 그대로 유지합니다.
  const [wizardStep, setWizardStep] = useState<1 | 2 | 3>(1)
  const [wizardSegnoTimingTouched, setWizardSegnoTimingTouched] = useState(false)
  const [autoSplitPreset, setAutoSplitPreset] = useState<AutoSplitPreset>('normal')
  const [autoSplitAnalyzing, setAutoSplitAnalyzing] = useState(false)
  const [autoSplitCandidates, setAutoSplitCandidates] = useState<Segno[] | null>(null)
  // 저장 요청이 진행 중인 동안 버튼 상태를 즉시 반영합니다.
  const [saving, setSaving] = useState(false)
  const [savedSnapshot, setSavedSnapshot] = useState(() => authoringSnapshot({
    sourceType: initialSourceType,
    sourceMode: initialSourceType,
    title: '',
    mediaFileId: null,
    youtubeUrl: '',
    segnos: [freshSegno(1)],
  }))
  const playbackVolume = usePlaybackVolume()
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const audioPreviewEndRef = useRef<number | null>(null)
  const localObjectUrlRef = useRef<string | null>(null)
  const previewDownloadAbortRef = useRef<AbortController | null>(null)
  const pendingUploadedFileRef = useRef<string | null>(null)
  const youtubePreviewRef = useRef<YouTubeAuthoringPreviewHandle | null>(null)
  const workspaceTopRef = useRef<HTMLDivElement | null>(null)
  const segnoListRef = useRef<HTMLDivElement | null>(null)
  const segnoFileInputRef = useRef<HTMLInputElement | null>(null)
  const autoSplitRunRef = useRef(0)
  // React state 반영 전의 연속 클릭까지 차단하는 동기식 single-flight guard입니다.
  const saveInFlightRef = useRef(false)

  const editing = editingSongId !== null
  // initialSongId가 있는 동안에는 편집 데이터를 불러오는 중이므로 신규 위저드를 잠깐 노출하지 않습니다.
  const newAuthoringWizard = !editing && initialSongId == null
  const currentSnapshot = useMemo(() => authoringSnapshot({
    sourceType,
    sourceMode,
    title,
    mediaFileId,
    youtubeUrl,
    segnos,
  }), [sourceType, sourceMode, title, mediaFileId, youtubeUrl, segnos])
  const hasUnsavedChanges = currentSnapshot !== savedSnapshot || (!editing && uploadProgress > 0 && uploadProgress < 100)

  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges)
  }, [hasUnsavedChanges, onUnsavedChangesChange])

  useEffect(() => () => {
    onUnsavedChangesChange?.(false)
  }, [onUnsavedChangesChange])

  useEffect(() => {
    if (!hasUnsavedChanges) return
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  useEffect(() => {
    if (appStatus && !appStatus.mediaApiEnabled && !editing) {
      setSourceType('YOUTUBE')
      setSourceMode('YOUTUBE')
    }
  }, [appStatus, editing])

  useEffect(() => {
    if (initialSongId == null) return
    let cancelled = false

    void api.getSong(initialSongId)
      .then(async (song) => {
        if (cancelled) return
        await editSong(song)
      })
      .catch((error) => {
        if (!cancelled) setMessage(userMessageForError(error, t('authoring.error.loadEdit')))
      })
      .finally(() => {
        if (!cancelled) onEditLoaded?.()
      })

    return () => { cancelled = true }
  }, [initialSongId])

  useEffect(() => () => {
    previewDownloadAbortRef.current?.abort()
    if (localObjectUrlRef.current) URL.revokeObjectURL(localObjectUrlRef.current)
  }, [])

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = playbackVolume.volume / 100
  }, [localPreview, playbackVolume.volume])

  function replaceAudioPreview(url: string | null, objectUrl = false) {
    // 음원이 바뀌면 이전 음원에서 계산한 자동 분할 후보는 재사용하지 않습니다.
    autoSplitRunRef.current += 1
    setAutoSplitAnalyzing(false)
    setAutoSplitCandidates(null)
    if (localObjectUrlRef.current) URL.revokeObjectURL(localObjectUrlRef.current)
    localObjectUrlRef.current = objectUrl ? url : null
    setLocalPreview(url)
    setFileDuration(null)
    setPreviewDownloadProgress(null)
    audioPreviewEndRef.current = null
  }

  function resetEditor(nextMessage = t('authoring.message.readyForNew')) {
    previewDownloadAbortRef.current?.abort()
    previewDownloadAbortRef.current = null
    replaceAudioPreview(null)
    pendingUploadedFileRef.current = null
    setEditingSongId(null)
    setEditingShareId(null)
    setPreviewDownloadState('idle')
    setUploadCopyConfirmed(false)
    const nextSourceType: SourceType = appStatus?.mediaApiEnabled ? 'FILE' : 'YOUTUBE'
    setSourceType(nextSourceType)
    setSourceMode(nextSourceType)
    setTitle('')
    setMediaFileId(null)
    setYoutubeUrl('')
    setYoutubeDuration(null)
    setUploadProgress(0)
    const nextSegnos = [freshSegno(1)]
    setSegnos(nextSegnos)
    setCopyrightConfirmed(false)
    setWizardStep(1)
    setWizardSegnoTimingTouched(false)
    setAutoSplitPreset('normal')
    setSavedSnapshot(authoringSnapshot({
      sourceType: nextSourceType,
      sourceMode: nextSourceType,
      title: '',
      mediaFileId: null,
      youtubeUrl: '',
      segnos: nextSegnos,
    }))
    setMessage(nextMessage)
  }

  async function discardPendingUpload(): Promise<boolean> {
    const pendingFileId = pendingUploadedFileRef.current
    if (!pendingFileId) return true

    try {
      await api.discardMediaFile(pendingFileId)
      pendingUploadedFileRef.current = null
      setMediaFileId(null)
      setUploadProgress(0)
      replaceAudioPreview(null)
      return true
    } catch (error) {
      setMessage(userMessageForError(error, t('authoring.error.discardUpload')))
      return false
    }
  }

  async function cancelNewWork(nextMessage = t('authoring.message.readyForNew')): Promise<boolean> {
    if (!await discardPendingUpload()) return false
    resetEditor(nextMessage)
    return true
  }

  async function editSong(song: Song) {
    previewDownloadAbortRef.current?.abort()
    const previewController = new AbortController()
    previewDownloadAbortRef.current = previewController

    if (pendingUploadedFileRef.current) {
      const discarded = await cancelNewWork(t('authoring.message.cancelUploadLoadExisting'))
      if (!discarded) return
    }
    replaceAudioPreview(null)
    setEditingSongId(song.id)
    setEditingShareId(song.shareId)
    setSourceType(song.sourceType)
    setSourceMode(song.sourceType)
    setTitle(song.title)
    setMediaFileId(song.mediaFileId ?? null)
    setYoutubeUrl(song.youtubeUrl ?? '')
    setYoutubeDuration(null)
    setUploadProgress(0)
    const nextSegnos = editableSegnos(song)
    setSegnos(nextSegnos)
    setCopyrightConfirmed(song.sourceType === 'FILE')
    setWizardStep(2)
    setWizardSegnoTimingTouched(true)
    setSavedSnapshot(authoringSnapshot({
      sourceType: song.sourceType,
      sourceMode: song.sourceType,
      title: song.title,
      mediaFileId: song.mediaFileId ?? null,
      youtubeUrl: song.youtubeUrl ?? '',
      segnos: nextSegnos,
    }))
    setMessage(t('authoring.message.loadedForEdit', { title: song.title }))

    if (song.sourceType === 'FILE' && song.mediaFileId) {
      const cacheKey = audioCacheKey(song.mediaFileId, song.shareId)
      try {
        const cached = await audioStore.get(cacheKey)
        if (cached) {
          replaceAudioPreview(URL.createObjectURL(cached), true)
          setPreviewDownloadProgress(100)
          setPreviewDownloadState('ready')
          setMessage(t('authoring.message.cachedAudioLoaded', { title: song.title }))
        } else {
          setPreviewDownloadProgress(null)
          setPreviewDownloadState('waiting')
          setMessage(t('authoring.message.downloadNeededForEdit', { title: song.title }))
        }
      } catch (error) {
        if (previewController.signal.aborted) return
        replaceAudioPreview(null)
        setPreviewDownloadProgress(null)
        setPreviewDownloadState('error')
        setMessage(userMessageForError(error, t('authoring.error.cachedAudio')))
      }
    }

    window.requestAnimationFrame(() => {
      workspaceTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
    })
  }

  async function loadSegnoFile(file: File | null) {
    if (!file || editing) return
    if (!file.name.toLowerCase().endsWith('.segno')) {
      setMessage(t('authoring.error.segnoExtension'))
      return
    }
    if (pendingUploadedFileRef.current) {
      const discarded = await discardPendingUpload()
      if (!discarded) return
    }

    try {
      const document = parseSegnoFile(await file.text())
      previewDownloadAbortRef.current?.abort()
      previewDownloadAbortRef.current = null
      replaceAudioPreview(null)
      pendingUploadedFileRef.current = null
      setEditingSongId(null)
      setEditingShareId(null)
      setPreviewDownloadState('idle')
      setUploadCopyConfirmed(false)
      setSourceMode('SEGNO_FILE')
      setSourceType(document.sourceType)
      setTitle(document.title)
      setMediaFileId(null)
      setYoutubeUrl(document.youtubeUrl ?? '')
      setYoutubeDuration(null)
      setUploadProgress(0)
      setSegnos(segnosFromFile(document))
      setCopyrightConfirmed(false)
      setWizardSegnoTimingTouched(true)
      setWizardStep(2)
      setMessage(document.sourceType === 'YOUTUBE'
        ? t('authoring.message.segnoFileYoutubeLoaded')
        : t('authoring.message.segnoFileAudioLoaded'))
    } catch (error) {
      setMessage(userMessageForError(error, t('authoring.error.segnoFileLoad')))
    }
  }

  function updateSegno(order: number, patch: Partial<Segno>) {
    if ('startTime' in patch || 'endTime' in patch) setWizardSegnoTimingTouched(true)
    setSegnos((current) => current.map((segno) =>
      segno.order === order ? { ...segno, ...patch } : segno,
    ))
  }

  function addSegno() {
    if (plan?.maxSegnosPerSong != null && segnos.length >= plan.maxSegnosPerSong) {
      setMessage(t('authoring.error.maxSegnos', { max: plan.maxSegnosPerSong }))
      return
    }
    setSegnos((current) => [...current, freshSegno(current.length + 1)])
    window.requestAnimationFrame(() => {
      segnoListRef.current?.scrollTo({ left: segnoListRef.current.scrollWidth, behavior: 'smooth' })
    })
  }

  function scrollSegnos(direction: -1 | 1) {
    const list = segnoListRef.current
    if (!list) return
    list.scrollBy({ left: direction * Math.max(300, list.clientWidth * 0.8), behavior: 'smooth' })
  }

  function shiftAllSegnosBy(delta: -0.1 | 0.1) {
    const result = shiftAllSegnoTimes(segnos, delta)
    if (!result.ok) {
      setMessage(t('authoring.message.shiftAllBeforeZero'))
      return
    }
    setWizardSegnoTimingTouched(true)
    setSegnos(result.segnos)
    setMessage(t(delta < 0
      ? 'authoring.message.shiftAllBackward'
      : 'authoring.message.shiftAllForward'))
  }

  async function analyzePracticeSegments() {
    if (sourceType !== 'FILE' || !localPreview) {
      setMessage(t('authoring.autoSplit.audioRequired'))
      return
    }
    if (autoSplitAnalyzing) return

    const runId = autoSplitRunRef.current + 1
    autoSplitRunRef.current = runId
    setAutoSplitAnalyzing(true)
    setAutoSplitCandidates(null)
    setMessage(t('authoring.autoSplit.analyzing'))

    let audioContext: AudioContext | null = null

    try {
      // 분석 중 표시가 먼저 화면에 그려진 뒤 FFT/구조 분석을 시작합니다.
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()))

      const response = await fetch(localPreview)
      if (!response.ok) throw new Error('AUTO_SPLIT_AUDIO_READ_FAILED')

      const encodedAudio = await response.arrayBuffer()
      audioContext = new AudioContext()
      const audioBuffer = await audioContext.decodeAudioData(encodedAudio.slice(0))

      if (runId !== autoSplitRunRef.current) return

      if (!Number.isFinite(audioBuffer.duration) || audioBuffer.duration < 0.2) {
        setMessage(t('authoring.autoSplit.audioTooShort'))
        return
      }

      if (audioBuffer.duration > 30 * 60) {
        setMessage(t('authoring.autoSplit.audioTooLong'))
        return
      }

      const candidates = await automaticSegnosFromMusicStructure(audioBuffer, autoSplitPreset)

      if (runId !== autoSplitRunRef.current) return

      if (plan?.maxSegnosPerSong != null && candidates.length > plan.maxSegnosPerSong) {
        setMessage(t('authoring.autoSplit.tooMany', { max: plan.maxSegnosPerSong }))
        return
      }

      if (candidates.length === 0) {
        setMessage(t('authoring.autoSplit.failed'))
        return
      }

      setAutoSplitCandidates(candidates)
      setMessage(t('authoring.autoSplit.ready', { count: candidates.length }))
    } catch {
      if (runId === autoSplitRunRef.current) {
        setMessage(t('authoring.autoSplit.failed'))
      }
    } finally {
      if (audioContext) {
        try {
          await audioContext.close()
        } catch {
          // 분석 결과와 무관한 close 실패는 사용자 작업을 막지 않습니다.
        }
      }
      if (runId === autoSplitRunRef.current) {
        setAutoSplitAnalyzing(false)
      }
    }
  }

  function applyAutomaticSegnos() {
    if (!autoSplitCandidates?.length) return

    const first = segnos[0]
    const untouchedDefault = segnos.length === 1
      && first?.title === 'Segno 01'
      && first.startTime === 0
      && first.endTime === 1
      && !(first.lyrics ?? '').trim()

    if (!untouchedDefault && !window.confirm(t('authoring.autoSplit.replaceConfirm'))) {
      return
    }

    setWizardSegnoTimingTouched(true)
    setSegnos(autoSplitCandidates)
    setAutoSplitCandidates(null)
    setMessage(t('authoring.autoSplit.applied', { count: autoSplitCandidates.length }))

    window.requestAnimationFrame(() => {
      segnoListRef.current?.scrollTo({ left: 0, behavior: 'smooth' })
    })
  }

  function mergeSegno(order: number, direction: 'previous' | 'next') {
    const result = mergeSegnoWithNeighbor(segnos, order, direction)
    if (!result.ok) return
    setWizardSegnoTimingTouched(true)
    setSegnos(result.segnos)
    setMessage(t(direction === 'previous'
      ? 'authoring.message.mergePrevious'
      : 'authoring.message.mergeNext'))
  }

  function splitCurrentSegno(order: number) {
    if (plan?.maxSegnosPerSong != null && segnos.length >= plan.maxSegnosPerSong) {
      setMessage(t('authoring.error.maxSegnos', { max: plan.maxSegnosPerSong }))
      return
    }

    const splitTime = currentPreviewTime()
    if (splitTime == null) return

    const result = splitSegnoAtTime(segnos, order, splitTime)
    if (!result.ok) {
      setMessage(t('authoring.message.splitOutside'))
      return
    }

    setWizardSegnoTimingTouched(true)
    setSegnos(result.segnos)
    setMessage(t('authoring.message.splitCurrent', { seconds: splitTime.toFixed(1) }))
  }

  function currentPreviewTime(): number | null {
    const currentTime = sourceType === 'YOUTUBE'
      ? youtubePreviewRef.current?.getCurrentTime()
      : audioRef.current?.currentTime
    if (currentTime == null) {
      setMessage(sourceType === 'YOUTUBE'
        ? t('authoring.message.youtubeSeekFirst')
        : t('authoring.message.audioSeekFirst'))
      return null
    }
    return roundTenth(currentTime)
  }

  function capture(order: number, target: 'startTime' | 'endTime') {
    const captured = currentPreviewTime()
    if (captured == null) return
    updateSegno(order, { [target]: captured })
    setMessage(t(target === 'startTime' ? 'authoring.message.captureStart' : 'authoring.message.captureEnd', { seconds: captured.toFixed(1) }))
  }

  function captureEndAndNextStart(order: number) {
    const captured = currentPreviewTime()
    if (captured == null) return
    setWizardSegnoTimingTouched(true)

    const existingNext = segnos
      .filter((segno) => segno.order > order)
      .sort((left, right) => left.order - right.order)[0]
    const nextOrder = existingNext?.order ?? (segnos.reduce((max, segno) => Math.max(max, segno.order), 0) + 1)

    setSegnos((current) => {
      const next = current
        .filter((segno) => segno.order > order)
        .sort((left, right) => left.order - right.order)[0]

      if (next) {
        return current.map((segno) => {
          if (segno.order === order) return { ...segno, endTime: captured }
          if (segno.order === next.order) return { ...segno, startTime: captured }
          return segno
        })
      }

      const createdOrder = current.reduce((max, segno) => Math.max(max, segno.order), 0) + 1
      const created = {
        ...freshSegno(createdOrder),
        startTime: captured,
        endTime: roundTenth(captured + 1),
      }
      return [
        ...current.map((segno) => segno.order === order ? { ...segno, endTime: captured } : segno),
        created,
      ]
    })

    setMessage(existingNext
      ? t('authoring.message.captureEndNextExisting', { seconds: captured.toFixed(1) })
      : t('authoring.message.captureEndNextCreated', { seconds: captured.toFixed(1) }))

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const nextEditor = segnoListRef.current?.querySelector<HTMLElement>(`[data-segno-order="${nextOrder}"]`)
        nextEditor?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
      })
    })
  }

  function previewSegno(segno: Segno) {
    if (sourceType === 'YOUTUBE') {
      const error = youtubePreviewRef.current?.previewRange(segno.startTime, segno.endTime)
      if (error) setMessage(error)
      return
    }

    const audio = audioRef.current
    if (!audio || !localPreview) {
      setMessage(t('authoring.message.audioPreviewRequired'))
      return
    }
    const rangeError = segmentRangeError(segno.startTime, segno.endTime, audio.duration)
    if (rangeError) {
      setMessage(t('authoring.error.segmentRange', { title: segno.title }))
      return
    }
    audioPreviewEndRef.current = segno.endTime
    audio.currentTime = segno.startTime
    void audio.play()
    setMessage(t('authoring.message.previewSegno', { title: segno.title }))
  }

  function onAudioPreviewTimeUpdate() {
    const audio = audioRef.current
    const endTime = audioPreviewEndRef.current
    if (!audio || endTime == null) return
    if (audio.currentTime >= endTime - 0.03) {
      audio.pause()
      audio.currentTime = endTime
      audioPreviewEndRef.current = null
      setMessage(t('authoring.message.previewFinished'))
    }
  }

  function seekLocalAudio(seconds: number) {
    const audio = audioRef.current
    if (!audio) return
    audio.currentTime = clampSeekTime(audio.currentTime, audio.duration, seconds)
    setMessage(t(seconds < 0 ? 'playback.seekBackward' : 'playback.seekForward', { seconds: Math.abs(seconds) }))
  }


  async function downloadExistingAudioForEditing() {
    if (!editing || !mediaFileId) return

    previewDownloadAbortRef.current?.abort()
    const controller = new AbortController()
    previewDownloadAbortRef.current = controller
    setPreviewDownloadState('downloading')
    setPreviewDownloadProgress(0)
    setMessage(t('authoring.message.downloadingExistingAudio'))

    try {
      const preview = await api.mediaPlayUrl(mediaFileId)
      const blob = await downloadAudioFully(preview.downloadUrl, (progress) => {
        if (!controller.signal.aborted) setPreviewDownloadProgress(progress.percent)
      }, controller.signal)
      const cacheKey = audioCacheKey(mediaFileId, editingShareId ?? mediaFileId)
      await audioStore.put(cacheKey, blob)
      if (controller.signal.aborted) return

      replaceAudioPreview(URL.createObjectURL(blob), true)
      setPreviewDownloadProgress(100)
      setPreviewDownloadState('ready')
      setMessage(t('authoring.message.existingAudioDownloaded'))
    } catch (error) {
      if (controller.signal.aborted) return
      replaceAudioPreview(null)
      setPreviewDownloadProgress(null)
      setPreviewDownloadState('error')
      setMessage(userMessageForError(error, t('authoring.error.existingAudioDownload')))
    }
  }

  function validateFileBeforeSave(): string | null {
    if (!mediaFileId) return t('authoring.error.uploadFirst')
    const duration = fileDuration ?? audioRef.current?.duration ?? null
    if (duration == null || !Number.isFinite(duration) || duration <= 0) {
      return t('authoring.error.audioDuration')
    }
    for (const segno of segnos) {
      const error = segmentRangeError(segno.startTime, segno.endTime, duration)
      if (error) return t('authoring.error.segmentRange', { title: segno.title || `Segno ${segno.order}` })
    }
    return null
  }

  function validateYouTubeBeforeSave(): string | null {
    if (!youtubeVideoId(youtubeUrl)) {
      return t('authoring.error.youtubeUrl')
    }
    const duration = youtubeDuration ?? youtubePreviewRef.current?.getDuration() ?? null
    if (duration == null || duration <= 0) {
      return t('authoring.error.youtubeDuration')
    }
    for (const segno of segnos) {
      const error = segmentRangeError(segno.startTime, segno.endTime, duration)
      if (error) return t('authoring.error.segmentRange', { title: segno.title || `Segno ${segno.order}` })
    }
    return null
  }

  async function uploadFile(file: File | undefined) {
    if (!file || editing) return
    if (!uploadCopyConfirmed || !copyrightConfirmed) {
      setMessage(t('authoring.error.uploadConfirmations'))
      return
    }
    const validationError = audioUploadError(file)
    if (validationError) {
      if (file.size <= 0) setMessage(t('authoring.error.audioEmpty'))
      else if (file.size > DEFAULT_MAX_AUDIO_BYTES) setMessage(t('authoring.error.audioTooLarge', { maxMb: Math.floor(DEFAULT_MAX_AUDIO_BYTES / 1024 / 1024) }))
      else setMessage(t('authoring.error.audioType'))
      return
    }

    if (pendingUploadedFileRef.current) {
      try {
        await api.discardMediaFile(pendingUploadedFileRef.current)
      } catch (error) {
        setMessage(userMessageForError(error, t('authoring.error.previousUploadCleanup')))
        return
      }
      pendingUploadedFileRef.current = null
    }

    setMessage(t('authoring.message.hashing'))
    setUploadProgress(0)
    setMediaFileId(null)
    replaceAudioPreview(URL.createObjectURL(file), true)

    let preparedFileId: string | null = null
    try {
      const sha256 = await sha256Hex(file)
      const prepared = await api.prepareUpload({
        originalName: file.name,
        contentType: normalizedAudioContentType(file),
        size: file.size,
        sha256,
        clientRequestId: `mioSegno-${crypto.randomUUID()}`,
        uploadRightsConfirmed: copyrightConfirmed,
        originalBackupConfirmed: uploadCopyConfirmed,
      })
      preparedFileId = prepared.fileId
      setMessage(t('authoring.message.uploading'))
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open(prepared.method, prepared.uploadUrl)
        Object.entries(prepared.requiredHeaders).forEach(([name, value]) => xhr.setRequestHeader(name, value))
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) setUploadProgress(Math.round(event.loaded / event.total * 100))
        }
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(`UPLOAD_HTTP_${xhr.status}`))
        xhr.onerror = () => reject(new Error('UPLOAD_NETWORK_FAILED'))
        xhr.send(file)
      })

      const completed = await api.completeUpload(prepared.uploadId)
      if (completed.status !== 'READY') throw new Error('MEDIA_NOT_READY')
      pendingUploadedFileRef.current = completed.fileId
      setMediaFileId(completed.fileId)
      if (completed.durationSeconds && completed.durationSeconds > 0) {
        setFileDuration(completed.durationSeconds)
      }
      // M4A는 AAC뿐 아니라 브라우저 호환성이 낮은 ALAC도 담을 수 있습니다.
      // Media API가 ALAC를 AAC로 정규화했을 수 있으므로 M4A만 canonical
      // server representation을 다시 받아 미리듣기/자동분할/IndexedDB가
      // 실제 공유 재생 파일과 같은 바이트를 사용하게 합니다.
      let cacheBlob: Blob = file
      if (shouldRefreshCanonicalAudioAfterUpload(file)) {
        const canonical = await api.mediaPlayUrl(completed.fileId)
        cacheBlob = await downloadAudioFully(canonical.downloadUrl, (progress) => {
          setUploadProgress(Math.min(99, Math.max(90, 90 + Math.round((progress.percent ?? 0) / 10))))
        })
        replaceAudioPreview(URL.createObjectURL(cacheBlob), true)
      }
      await audioStore.put(audioCacheKey(completed.fileId, completed.fileId), cacheBlob).catch(() => undefined)
      setUploadProgress(100)
      setMessage(t('authoring.message.uploadComplete'))
    } catch (error) {
      setMediaFileId(null)
      pendingUploadedFileRef.current = null
      if (preparedFileId) {
        try {
          // PUT 또는 complete가 실패했어도 발급된 파일 레코드가 남지 않도록 정리합니다.
          await api.discardMediaFile(preparedFileId)
        } catch {
          // 원래 업로드 오류를 사용자에게 우선 안내합니다. 만료 세션 정리는 서버가 수행합니다.
        }
      }
      setMessage(userMessageForError(error, t('authoring.error.uploadFailed')))
    }
  }


  function chooseWizardSource(nextSource: SourceType) {
    setSourceMode(nextSource)
    setSourceType(nextSource)
    setWizardSegnoTimingTouched(false)
    setMessage('')
    setWizardStep(2)
    window.requestAnimationFrame(() => {
      workspaceTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
    })
  }

  async function returnToWizardSourceChoice() {
    if (pendingUploadedFileRef.current) {
      const discarded = await discardPendingUpload()
      if (!discarded) return
    }
    resetEditor('')
    setWizardStep(1)
    setMessage('')
    window.requestAnimationFrame(() => {
      workspaceTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
    })
  }

  function openWizardReview() {
    if (!title.trim()) {
      setMessage(t('authoring.wizard.titleRequired'))
      return
    }
    if (!wizardSegnoTimingTouched && sourceMode !== 'SEGNO_FILE') {
      setMessage(t('authoring.wizard.timingRequired'))
      return
    }
    const validationError = sourceType === 'YOUTUBE'
      ? validateYouTubeBeforeSave()
      : validateFileBeforeSave()
    if (validationError) {
      setMessage(validationError)
      return
    }
    setMessage('')
    setWizardStep(3)
    window.requestAnimationFrame(() => {
      workspaceTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
    })
  }

  async function saveSong(startPracticeAfterCreate = false) {
    // 첫 저장 요청이 끝나기 전의 재호출은 즉시 무시합니다.
    // 버튼 disabled만으로는 React state 반영 전의 빠른 연속 클릭을 완전히 막을 수 없으므로
    // ref를 먼저 잠가 동일 저장 요청이 두 번 전송되지 않도록 합니다.
    if (saveInFlightRef.current) return
    saveInFlightRef.current = true
    setSaving(true)

    try {
      const validationError = sourceType === 'YOUTUBE'
        ? validateYouTubeBeforeSave()
        : validateFileBeforeSave()
      if (validationError) {
        setMessage(validationError)
        return
      }
      if (!editing && sourceType === 'FILE' && !copyrightConfirmed) {
        setMessage(t('authoring.error.rightsRequired'))
        return
      }

      if (editingSongId !== null) {
        const saved = await api.updateSong(editingSongId, {
          title,
          segnos: songSegnoPayload(segnos),
        })
        resetEditor(t('authoring.message.editSaved', { title: saved.title, date: new Date(saved.expiresAt).toLocaleDateString(locale) }))
      } else {
        const saved = await api.createSong({
          title,
          sourceType,
          mediaFileId: sourceType === 'FILE' ? mediaFileId : null,
          youtubeUrl: sourceType === 'YOUTUBE' ? canonicalYoutubeUrl(youtubeUrl) : null,
          copyrightConfirmed: sourceType === 'FILE' ? copyrightConfirmed : false,
          segnos: songSegnoPayload(segnos),
        })
        // 곡 저장이 성공한 뒤에는 이 파일이 더 이상 임시 업로드가 아니므로
        // 편집기 초기화 과정에서 삭제하지 않습니다.
        pendingUploadedFileRef.current = null
        if (startPracticeAfterCreate && onCreatedPractice) {
          // 신규 위저드의 마지막 버튼은 저장 성공 후 곧바로 소유자 연습실로 연결합니다.
          onUnsavedChangesChange?.(false)
          onCreatedPractice(saved.id)
          return
        }
        const savedMessage = t('authoring.message.created', { title: saved.title, date: new Date(saved.expiresAt).toLocaleDateString(locale) })
        resetEditor(savedMessage)
      }
    } catch (error) {
      setMessage(userMessageForError(error, t('authoring.error.save')))
    } finally {
      // 성공/실패/검증 중단 모두 다음 정상 저장을 허용하도록 잠금을 해제합니다.
      saveInFlightRef.current = false
      setSaving(false)
    }
  }

  if (newAuthoringWizard && wizardStep === 1) {
    return (
      <section className="workspace-grid workspace-grid-single">
        <article className="card workspace-main authoring-wizard-card">
          <div className="authoring-wizard-heading" ref={workspaceTopRef}>
            <span className="authoring-wizard-step">{t('authoring.wizard.step', { current: 1 })}</span>
            <h2>{t('authoring.wizard.sourceTitle')}</h2>
            <p className="muted">{t('authoring.wizard.sourceDescription')}</p>
          </div>

          <div className="authoring-wizard-source-options">
            <button
              type="button"
              className="button primary large authoring-wizard-source-button"
              disabled={!appStatus?.mediaApiEnabled}
              title={!appStatus?.mediaApiEnabled ? t('authoring.source.fileUnavailable') : undefined}
              onClick={() => chooseWizardSource('FILE')}
            >
              <span aria-hidden="true">🎵</span>
              <strong>{t('authoring.wizard.audioChoice')}</strong>
            </button>
            <button
              type="button"
              className="button secondary large authoring-wizard-source-button"
              onClick={() => chooseWizardSource('YOUTUBE')}
            >
              <span aria-hidden="true">▶</span>
              <strong>{t('authoring.wizard.youtubeChoice')}</strong>
            </button>
          </div>

          <div className="authoring-wizard-import">
            <button type="button" className="button ghost" onClick={() => segnoFileInputRef.current?.click()}>
              {t('authoring.wizard.segnoFileChoice')}
            </button>
            <input
              ref={segnoFileInputRef}
              type="file"
              accept=".segno,application/vnd.miosegno+json"
              hidden
              onChange={(event) => {
                const file = event.currentTarget.files?.[0] ?? null
                event.currentTarget.value = ''
                void loadSegnoFile(file)
              }}
            />
          </div>

          {!appStatus?.mediaApiEnabled && (
            <p className="notice">{t('authoring.notice.fileUnavailable')}</p>
          )}
          {message && <p className="notice">{message}</p>}

          <NonPersonalizedAdSlot
            status={appStatus}
            eligible={ownerAdsEligible(plan)}
            placement="owner-authoring"
          />
        </article>
      </section>
    )
  }

  if (newAuthoringWizard && wizardStep === 3) {
    return (
      <section className="workspace-grid workspace-grid-single">
        <article className="card workspace-main authoring-wizard-card">
          <div className="authoring-wizard-heading" ref={workspaceTopRef}>
            <span className="authoring-wizard-step">{t('authoring.wizard.step', { current: 3 })}</span>
            <h2>{t('authoring.wizard.reviewTitle')}</h2>
            <p className="muted">{t('authoring.wizard.reviewDescription')}</p>
          </div>

          <div className="authoring-wizard-review">
            <div>
              <span>{t('authoring.field.songTitle')}</span>
              <strong>{title.trim()}</strong>
            </div>
            <div>
              <span>{t('authoring.wizard.sourceLabel')}</span>
              <strong>{sourceMode === 'SEGNO_FILE'
                ? t('authoring.wizard.segnoFileShort')
                : sourceType === 'FILE'
                  ? t('authoring.wizard.audioShort')
                  : t('authoring.wizard.youtubeShort')}</strong>
            </div>
            <div>
              <span>{t('authoring.wizard.segnoCountLabel')}</span>
              <strong>{t('authoring.wizard.segnoCount', { count: segnos.length })}</strong>
            </div>
          </div>

          <div className="authoring-wizard-final-actions">
            <button
              type="button"
              className="button primary large"
              disabled={saving}
              aria-busy={saving}
              onClick={() => void saveSong(true)}
            >
              {saving ? t('common.saving') : `▶ ${t('authoring.wizard.saveAndPractice')}`}
            </button>
            <button type="button" className="button ghost" disabled={saving} onClick={() => {
              setMessage('')
              setWizardStep(2)
            }}>
              ← {t('authoring.wizard.editSegnos')}
            </button>
          </div>

          {message && <p className="notice">{message}</p>}

          <NonPersonalizedAdSlot
            status={appStatus}
            eligible={ownerAdsEligible(plan)}
            placement="owner-authoring"
          />
        </article>
      </section>
    )
  }

  return (
    <section className="workspace-grid workspace-grid-single">
      <article className="card workspace-main">
        <div className={newAuthoringWizard ? 'authoring-wizard-heading' : 'section-title-row'} ref={workspaceTopRef}>
          {newAuthoringWizard ? (
            <>
              <span className="authoring-wizard-step">{t('authoring.wizard.step', { current: 2 })}</span>
              <h2>{t('authoring.wizard.segmentTitle')}</h2>
              <p className="muted">{t('authoring.wizard.segmentDescription')}</p>
              <span className="authoring-wizard-definition">{t('authoring.wizard.segnoDefinition')}</span>
              <div className="authoring-wizard-segno-guide" aria-label={t('authoring.wizard.guideTitle')}>
                <strong>{t('authoring.wizard.guideTitle')}</strong>
                <ol>
                  <li>{t('authoring.wizard.guidePlay')}</li>
                  <li>{t('authoring.wizard.guideStart')}</li>
                  <li>{t('authoring.wizard.guideEnd')}</li>
                  <li>{t('authoring.wizard.guideRepeat')}</li>
                </ol>
                <details className="authoring-wizard-end-next-help">
                  <summary>{t('authoring.wizard.endNextHelpTitle')}</summary>
                  <p>{t('authoring.wizard.endNextHelpBody')}</p>
                </details>
              </div>
            </>
          ) : (
            <>
              <div>
                <p className="eyebrow">{t('authoring.header.eyebrow')}</p>
                <h2>{editing ? t('authoring.header.editTitle') : t('authoring.header.createTitle')}</h2>
              </div>
              <div className="control-row">
                {editing && <span className="badge success">{t('authoring.badge.editing')}</span>}
                <span className="badge">{plan
                  ? (plan.maxSegnosPerSong == null ? t('authoring.plan.unlimited') : t('authoring.plan.maxSegnos', { max: plan.maxSegnosPerSong }))
                  : t('authoring.plan.loading')}</span>
              </div>
            </>
          )}
        </div>

        <NonPersonalizedAdSlot
          status={appStatus}
          eligible={ownerAdsEligible(plan)}
          placement="owner-authoring"
        />

        <label>
          {t('authoring.field.songTitle')}
          <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder={t('authoring.field.songTitlePlaceholder')} />
        </label>

        {editing ? (
          <div className="source-switch">
            <button
              type="button"
              className={sourceMode === 'FILE' ? 'button primary' : 'button secondary'}
              disabled
              title={t('authoring.source.editLocked')}
            >
              {t('authoring.source.audioFile')}
            </button>
            <button
              type="button"
              className={sourceMode === 'YOUTUBE' ? 'button primary' : 'button secondary'}
              disabled
              title={t('authoring.source.editLocked')}
            >
              {t('authoring.source.youtubeLink')}
            </button>
            <button
              type="button"
              className={sourceMode === 'SEGNO_FILE' ? 'button primary' : 'button secondary'}
              disabled
              title={t('authoring.source.segnoFileEditLocked')}
            >
              {t('authoring.source.segnoFile')}
            </button>
          </div>
        ) : (
          <div className="authoring-wizard-source-summary">
            <span className="badge">
              {sourceMode === 'SEGNO_FILE'
                ? t('authoring.wizard.segnoFileShort')
                : sourceType === 'FILE'
                  ? t('authoring.wizard.audioShort')
                  : t('authoring.wizard.youtubeShort')}
            </span>
            <button type="button" className="button ghost" onClick={() => void returnToWizardSourceChoice()}>
              ← {t('authoring.wizard.changeSource')}
            </button>
          </div>
        )}

        {editing && (
          <p className="notice">{t('authoring.notice.editing')}</p>
        )}

        {!editing && !appStatus?.mediaApiEnabled && (
          <p className="notice">{t('authoring.notice.fileUnavailable')}</p>
        )}

        {sourceType === 'FILE' ? (
          <div className="upload-box compact-media-source">
            {!editing && !mediaFileId && (
              <div className="media-retention-warning notice warning">
                <strong>{t('authoring.upload.notBackupTitle')}</strong>
                <p>{t('authoring.upload.retentionPolicy')}</p>
                <p className="upload-evidence-notice">
                  {t('authoring.upload.evidenceNotice')} {' '}
                  <a href={legalDocumentUrl('https://www.2n4u.co.kr/legal/miosegno/privacy', locale)} target="_blank" rel="noreferrer">{t('authoring.upload.privacyLink')}</a>
                  {t('authoring.upload.privacySuffix')}
                </p>
                <label className="media-copy-confirmation">
                  <input type="checkbox" checked={copyrightConfirmed} onChange={(event) => setCopyrightConfirmed(event.target.checked)} />
                  <span>{t('authoring.upload.rightsConfirm')}</span>
                </label>
                <label className="media-copy-confirmation">
                  <input type="checkbox" checked={uploadCopyConfirmed} onChange={(event) => setUploadCopyConfirmed(event.target.checked)} />
                  <span>{t('authoring.upload.backupConfirm')}</span>
                </label>
              </div>
            )}
            {!editing && (
              <div className="authoring-audio-file-choice">
                <p className="muted">{t('authoring.upload.chooseFileHelp')}</p>
                <p className="muted"><small>{t('authoring.upload.supportedFormats')}</small></p>
                <label className={uploadCopyConfirmed && copyrightConfirmed ? 'button primary large' : 'button primary large disabled'} aria-disabled={!uploadCopyConfirmed || !copyrightConfirmed}>
                  {t('authoring.upload.chooseCopy')}
                  <input
                    type="file"
                    hidden
                    disabled={!uploadCopyConfirmed || !copyrightConfirmed}
                    accept="audio/mpeg,audio/mp4,audio/aac,audio/flac,audio/ogg,audio/wav,audio/x-wav,audio/webm,.mp3,.m4a,.aac,.flac,.ogg,.wav,.webm"
                    onChange={(event) => void uploadFile(event.target.files?.[0])}
                  />
                </label>
              </div>
            )}
            {editing && <small className="muted">{t('authoring.upload.existingKept')}</small>}
            <progress max={100} value={uploadProgress} />
            {editing && previewDownloadState === 'waiting' && !localPreview && (
              <div className="audio-download-required notice warning">
                <strong>{t('authoring.preview.downloadRequiredTitle')}</strong>
                <span>{t('authoring.preview.downloadRequiredBody')}</span>
                <button type="button" className="button primary" onClick={() => void downloadExistingAudioForEditing()}>{t('authoring.preview.downloadAudio')}</button>
              </div>
            )}
            {editing && previewDownloadState === 'downloading' && previewDownloadProgress !== null && !localPreview && (
              <div className="audio-download-status" role="status" aria-live="polite">
                <progress max={100} value={previewDownloadProgress} />
                <span>{t('authoring.preview.downloading', { percent: previewDownloadProgress })}</span>
              </div>
            )}
            {editing && previewDownloadState === 'error' && !localPreview && (
              <button type="button" className="button secondary" onClick={() => void downloadExistingAudioForEditing()}>{t('authoring.preview.retryDownload')}</button>
            )}
            {mediaFileId && !editing && (
              <div className="control-row media-upload-ready-row">
                <span className="badge success">{t('authoring.upload.completeBadge')}</span>
                <button
                  type="button"
                  className="button danger ghost"
                  onClick={() => void cancelNewWork(t('authoring.message.uploadDeleted'))}
                >
                  {t('authoring.upload.cancel')}
                </button>
              </div>
            )}
            {localPreview && (
              <div className="authoring-audio-preview">
                <audio
                  ref={audioRef}
                  src={localPreview}
                  controls
                  preload="metadata"
                  onLoadedMetadata={(event) => {
                    const duration = event.currentTarget.duration
                    if (Number.isFinite(duration) && duration > 0) setFileDuration(duration)
                  }}
                  onTimeUpdate={onAudioPreviewTimeUpdate}
                  onEnded={() => { audioPreviewEndRef.current = null }}
                />
                <PlaybackSeekControls
                  onSeek={seekLocalAudio}
                  onPlay={() => {
                    audioPreviewEndRef.current = null
                    if (audioRef.current) void audioRef.current.play()
                  }}
                  onStop={() => {
                    audioPreviewEndRef.current = null
                    audioRef.current?.pause()
                  }}
                  disabled={!localPreview}
                  playLabel={t('authoring.preview.audioPlay')}
                  stopLabel={t('authoring.preview.audioStop')}
                />
                <VolumeControl
                  volume={playbackVolume.volume}
                  muted={playbackVolume.muted}
                  onVolumeChange={playbackVolume.setVolume}
                  onToggleMute={playbackVolume.toggleMute}
                />
                {fileDuration && <small className="muted">{t('authoring.preview.audioDuration', { seconds: fileDuration.toFixed(1) })}</small>}
              </div>
            )}
          </div>
        ) : (
          <div className="youtube-input-block">
            <label>
              {t('authoring.field.youtubeUrl')}
              <input
                value={youtubeUrl}
                disabled={editing}
                onChange={(event) => {
                  setYoutubeUrl(event.target.value)
                  setYoutubeDuration(null)
                }}
                onBlur={() => {
                  const canonical = canonicalYoutubeUrl(youtubeUrl)
                  if (canonical && canonical !== youtubeUrl.trim()) {
                    setYoutubeUrl(canonical)
                  }
                }}
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </label>
            <YouTubeAuthoringPreview
              ref={youtubePreviewRef}
              youtubeUrl={youtubeUrl}
              title={title}
              onDurationChange={setYoutubeDuration}
              onMessage={setMessage}
              volume={playbackVolume.volume}
              muted={playbackVolume.muted}
              onVolumeChange={playbackVolume.setVolume}
              onToggleMute={playbackVolume.toggleMute}
            />
            {appStatus?.demoMode && !editing && (
              <button
                type="button"
                className="button secondary"
                onClick={() => {
                  setTitle(t('authoring.demo.songTitle'))
                  setYoutubeUrl('https://www.youtube.com/watch?v=M7lc1UVf-VE')
                  setSegnos([
                    { order: 1, title: 'Segno 01', startTime: 0, endTime: 5, lyrics: t('authoring.demo.lyrics1') },
                    { order: 2, title: 'Segno 02', startTime: 7, endTime: 12, lyrics: t('authoring.demo.lyrics2') },
                  ])
                  setWizardSegnoTimingTouched(true)
                  setMessage(t('authoring.demo.filled'))
                }}
              >
                {t('authoring.demo.fill')}
              </button>
            )}
          </div>
        )}

        <div className="section-title-row segno-section-header authoring-segno-toolbar">
          <h3>{t('authoring.segno.sectionTitle')}</h3>
          <div className="control-row segno-shift-controls">
            <button type="button" className="button secondary" onClick={() => shiftAllSegnosBy(-0.1)}>
              {t('authoring.segno.shiftAllBackward')}
            </button>
            <button type="button" className="button secondary" onClick={() => shiftAllSegnosBy(0.1)}>
              {t('authoring.segno.shiftAllForward')}
            </button>
          </div>
          <div className="control-row segno-scroll-controls">
            <button type="button" className="button ghost icon-button" aria-label={t('authoring.segno.previous')} onClick={() => scrollSegnos(-1)}>←</button>
            <button type="button" className="button ghost icon-button" aria-label={t('authoring.segno.next')} onClick={() => scrollSegnos(1)}>→</button>
            <button type="button" className="button secondary" onClick={addSegno}>{t('authoring.segno.add')}</button>
          </div>
        </div>

        {editing && (
          <p className="muted authoring-segno-workspace-help">{t('authoring.segno.workspaceHelp')}</p>
        )}

        {sourceType === 'FILE' && localPreview && (
          <div className="notice">
            <strong>{t('authoring.autoSplit.title')}</strong>
            <div className="control-row">
              <span>{t('authoring.autoSplit.length')}</span>
              <div className="control-row" role="group" aria-label={t('authoring.autoSplit.length')}>
                <button
                  type="button"
                  className={autoSplitPreset === 'short' ? 'button primary' : 'button secondary'}
                  aria-pressed={autoSplitPreset === 'short'}
                  disabled={autoSplitAnalyzing}
                  onClick={() => {
                    setAutoSplitPreset('short')
                    setAutoSplitCandidates(null)
                  }}
                >
                  {t('authoring.autoSplit.short')}
                </button>
                <button
                  type="button"
                  className={autoSplitPreset === 'normal' ? 'button primary' : 'button secondary'}
                  aria-pressed={autoSplitPreset === 'normal'}
                  disabled={autoSplitAnalyzing}
                  onClick={() => {
                    setAutoSplitPreset('normal')
                    setAutoSplitCandidates(null)
                  }}
                >
                  {t('authoring.autoSplit.normal')}
                </button>
                <button
                  type="button"
                  className={autoSplitPreset === 'long' ? 'button primary' : 'button secondary'}
                  aria-pressed={autoSplitPreset === 'long'}
                  disabled={autoSplitAnalyzing}
                  onClick={() => {
                    setAutoSplitPreset('long')
                    setAutoSplitCandidates(null)
                  }}
                >
                  {t('authoring.autoSplit.long')}
                </button>
              </div>
              <button
                type="button"
                className="button primary"
                disabled={autoSplitAnalyzing}
                onClick={() => void analyzePracticeSegments()}
              >
                {t(autoSplitAnalyzing ? 'authoring.autoSplit.analyzing' : 'authoring.autoSplit.analyze')}
              </button>
            </div>

            {autoSplitCandidates && (
              <div className="control-row" role="status" aria-live="polite">
                <span>{t('authoring.autoSplit.preview', { count: autoSplitCandidates.length })}</span>
                <button type="button" className="button primary" onClick={applyAutomaticSegnos}>
                  {t('authoring.autoSplit.apply')}
                </button>
                <button type="button" className="button secondary" onClick={() => setAutoSplitCandidates(null)}>
                  {t('authoring.autoSplit.cancel')}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="segno-editor-list" ref={segnoListRef}>
          {segnos.map((segno, segnoIndex) => (
            <article className="segno-editor compact-segno-editor" data-segno-order={segno.order} key={segno.order}>
              <div className="segno-editor-toolbar">
                <label className="segno-name-field">
                  <span>{t('authoring.segno.name')}</span>
                  <input
                    aria-label={t('authoring.segno.nameAria', { order: segno.order })}
                    value={segno.title}
                    onChange={(event) => updateSegno(segno.order, { title: event.target.value })}
                  />
                </label>
                <div className="control-row segno-editor-actions">
                  {(sourceType === 'YOUTUBE' || localPreview) && (
                    <button type="button" className="button secondary" onClick={() => previewSegno(segno)}>
                      {t('authoring.segno.preview')}
                    </button>
                  )}
                  {segnos.length > 1 && (
                    <button
                      type="button"
                      className="button danger ghost"
                      onClick={() => setSegnos((current) => current
                        .filter((value) => value.order !== segno.order)
                        .map((value, index) => ({ ...value, order: index + 1 })))}
                    >
                      {t('common.delete')}
                    </button>
                  )}
                </div>
              </div>

              <div className="time-editor compact-time-editor">
                <div>
                  <label htmlFor={`segno-start-${segno.order}`}>{t('authoring.segno.startLabel')}</label>
                  <div className="time-value-row">
                    <button type="button" onClick={() => updateSegno(segno.order, { startTime: nudge(segno.startTime, -0.1) })}>−0.1</button>
                    <input id={`segno-start-${segno.order}`} type="number" min="0" step="0.1" value={segno.startTime} onChange={(event) => updateSegno(segno.order, { startTime: roundTenth(Number(event.target.value)) })} />
                    <button type="button" onClick={() => updateSegno(segno.order, { startTime: nudge(segno.startTime, 0.1) })}>+0.1</button>
                  </div>
                  <div className="capture-buttons single-capture-button">
                    <button type="button" onClick={() => capture(segno.order, 'startTime')}>{t('authoring.segno.captureStart')}</button>
                  </div>
                </div>
                <div>
                  <label htmlFor={`segno-end-${segno.order}`}>{t('authoring.segno.endLabel')}</label>
                  <div className="time-value-row">
                    <button type="button" onClick={() => updateSegno(segno.order, { endTime: nudge(segno.endTime, -0.1) })}>−0.1</button>
                    <input id={`segno-end-${segno.order}`} type="number" min="0.1" step="0.1" value={segno.endTime} onChange={(event) => updateSegno(segno.order, { endTime: roundTenth(Number(event.target.value)) })} />
                    <button type="button" onClick={() => updateSegno(segno.order, { endTime: nudge(segno.endTime, 0.1) })}>+0.1</button>
                  </div>
                  <div className="capture-buttons end-capture-buttons">
                    <button type="button" onClick={() => capture(segno.order, 'endTime')}>{t('authoring.segno.captureEnd')}</button>
                    <button
                      type="button"
                      onClick={() => captureEndAndNextStart(segno.order)}
                    >
                      {t('authoring.segno.captureEndNext')}
                    </button>
                  </div>
                </div>
              </div>

              <div className="control-row segno-structure-controls">
                <button
                  type="button"
                  className="button secondary"
                  disabled={segnoIndex === 0}
                  onClick={() => mergeSegno(segno.order, 'previous')}
                >
                  {t('authoring.segno.mergePrevious')}
                </button>
                <button
                  type="button"
                  className="button secondary"
                  onClick={() => splitCurrentSegno(segno.order)}
                >
                  {t('authoring.segno.splitCurrent')}
                </button>
                <button
                  type="button"
                  className="button secondary"
                  disabled={segnoIndex === segnos.length - 1}
                  onClick={() => mergeSegno(segno.order, 'next')}
                >
                  {t('authoring.segno.mergeNext')}
                </button>
              </div>

              <label>
                {t('authoring.segno.lyrics')}
                <textarea
                  value={segno.lyrics ?? ''}
                  onChange={(event) => updateSegno(segno.order, { lyrics: event.target.value })}
                  placeholder={t('authoring.segno.lyricsPlaceholder')}
                />
                <small className="muted authoring-segno-lyrics-help">{t('authoring.segno.lyricsHelp')}</small>
              </label>
            </article>
          ))}
        </div>

        {sourceType === 'FILE' && editing && (
          <p className="muted">{t('authoring.upload.priorConfirmation')}</p>
        )}


        {editing ? (
          <div className="control-row save-action-row">
            <button
              type="button"
              className="button primary large"
              disabled={saving}
              aria-busy={saving}
              onClick={() => void saveSong()}
            >
              {saving ? t('common.saving') : t('authoring.action.saveEdit')}
            </button>
            <button type="button" className="button ghost large" disabled={saving} onClick={() => resetEditor(t('authoring.message.editCancelled'))}>{t('authoring.action.cancelEdit')}</button>
          </div>
        ) : (
          <div className="authoring-wizard-next-row">
            <button type="button" className="button primary large" onClick={openWizardReview}>
              {t('authoring.wizard.next')} →
            </button>
            {pendingUploadedFileRef.current && (
              <button type="button" className="button ghost" onClick={() => void cancelNewWork(t('authoring.message.newCancelled'))}>{t('authoring.action.cancelNew')}</button>
            )}
          </div>
        )}
        {message && <p className="notice">{message}</p>}

      </article>

    </section>
  )
}
